import type { BlockType } from '../constants.js';
import type { SectionSplit } from './sections.js';

// =====================================================================
// Layout template registry.
// =====================================================================
//
// Each template is a starting point for a new page. Users pick one,
// the page gets seeded with the template's sections (each filled with
// the template's chosen blocks using `blockDefaults`). After that,
// the user is free to edit content, add/delete/reorder sections, and
// move blocks across columns.
//
// The internal `legacy-stack` template ID identifies pages that were
// auto-migrated from v1 flat-blocks shape. It has no section recipe
// (migration uses the page's existing blocks as-is, wrapped in a
// single full-width section).

export type LayoutTemplateId =
  | 'service-business'
  | 'restaurant'
  | 'salon'
  | 'contractor'
  | 'solo-pro'
  | 'minimal'
  // Internal sentinels — set programmatically, never exposed in the
  // user-facing layouts picker.
  | 'legacy-stack' // v1 page migrated by normalize on read
  | 'starter-stack' // owner picked "Start with a starter stack" on the empty-page chooser
  | 'ai-generated' // page produced by the AI chat flow (3B-3.5e+)
  | 'sub-page-default'; // generic sub-page skeleton (hero + body + cta + footer)

export interface TemplateColumnSpec {
  // Block types to seed in this column, in order. Picker filters
  // ensure only allowed types appear here.
  blockTypes: BlockType[];
}

export interface TemplateSectionSpec {
  // Stable slot key — used by "Change layout" diffing to map content
  // across templates (e.g. both 'service-business' and 'salon' have
  // a 'hero' slot, so content carries over by key).
  slotKey: string;
  split: SectionSplit;
  columns: TemplateColumnSpec[];
}

export interface LayoutTemplate {
  id: LayoutTemplateId;
  label: string;
  description: string;
  // Section recipe used to seed a brand-new page. Empty for
  // 'legacy-stack' (migration uses the page's existing blocks).
  sections: TemplateSectionSpec[];
}

// User-facing template set (excludes 'legacy-stack').
export const LAYOUT_TEMPLATES: readonly LayoutTemplate[] = [
  {
    id: 'service-business',
    label: 'Service business',
    description: 'Hero, services grid, testimonials, contact — covers a typical service business.',
    sections: [
      { slotKey: 'hero', split: 'full', columns: [{ blockTypes: ['hero'] }] },
      {
        slotKey: 'services-cta',
        split: '2-1',
        columns: [{ blockTypes: ['services'] }, { blockTypes: ['cta'] }],
      },
      { slotKey: 'testimonials', split: 'full', columns: [{ blockTypes: ['testimonials'] }] },
      {
        slotKey: 'about-contact',
        split: '2-1',
        columns: [{ blockTypes: ['about'] }, { blockTypes: ['contact'] }],
      },
      { slotKey: 'footer', split: 'full', columns: [{ blockTypes: ['footer'] }] },
    ],
  },
  {
    id: 'restaurant',
    label: 'Restaurant / Cafe',
    description: 'Hero, menu, about + photos, contact — built for food businesses.',
    sections: [
      { slotKey: 'hero', split: 'full', columns: [{ blockTypes: ['hero'] }] },
      { slotKey: 'menu', split: 'full', columns: [{ blockTypes: ['services'] }] },
      {
        slotKey: 'about-gallery',
        split: '2-1',
        columns: [{ blockTypes: ['about'] }, { blockTypes: ['gallery'] }],
      },
      { slotKey: 'contact', split: 'full', columns: [{ blockTypes: ['contact'] }] },
      { slotKey: 'footer', split: 'full', columns: [{ blockTypes: ['footer'] }] },
    ],
  },
  {
    id: 'salon',
    label: 'Salon / Spa',
    description: 'Hero, about + photos, services, testimonials, contact — for personal-care businesses.',
    sections: [
      { slotKey: 'hero', split: 'full', columns: [{ blockTypes: ['hero'] }] },
      {
        slotKey: 'about-gallery',
        split: '1-1',
        columns: [{ blockTypes: ['about'] }, { blockTypes: ['gallery'] }],
      },
      { slotKey: 'services', split: 'full', columns: [{ blockTypes: ['services'] }] },
      { slotKey: 'testimonials', split: 'full', columns: [{ blockTypes: ['testimonials'] }] },
      {
        slotKey: 'contact-cta',
        split: '2-1',
        columns: [{ blockTypes: ['contact'] }, { blockTypes: ['cta'] }],
      },
      { slotKey: 'footer', split: 'full', columns: [{ blockTypes: ['footer'] }] },
    ],
  },
  {
    id: 'contractor',
    label: 'Contractor / Trades',
    description: 'Hero, services, project gallery, FAQ + CTA, contact — for trades and contractors.',
    sections: [
      { slotKey: 'hero', split: 'full', columns: [{ blockTypes: ['hero'] }] },
      { slotKey: 'services', split: 'full', columns: [{ blockTypes: ['services'] }] },
      { slotKey: 'gallery', split: 'full', columns: [{ blockTypes: ['gallery'] }] },
      {
        slotKey: 'faq-cta',
        split: '2-1',
        columns: [{ blockTypes: ['faq'] }, { blockTypes: ['cta'] }],
      },
      { slotKey: 'contact', split: 'full', columns: [{ blockTypes: ['contact'] }] },
      { slotKey: 'footer', split: 'full', columns: [{ blockTypes: ['footer'] }] },
    ],
  },
  {
    id: 'solo-pro',
    label: 'Solo professional',
    description: 'Hero, about, services, three testimonials, contact — perfect for solo operators.',
    sections: [
      { slotKey: 'hero', split: 'full', columns: [{ blockTypes: ['hero'] }] },
      { slotKey: 'about', split: 'full', columns: [{ blockTypes: ['about'] }] },
      { slotKey: 'services', split: 'full', columns: [{ blockTypes: ['services'] }] },
      {
        slotKey: 'testimonials',
        split: '1-1-1',
        columns: [
          { blockTypes: ['testimonials'] },
          { blockTypes: ['testimonials'] },
          { blockTypes: ['testimonials'] },
        ],
      },
      { slotKey: 'contact', split: 'full', columns: [{ blockTypes: ['contact'] }] },
      { slotKey: 'footer', split: 'full', columns: [{ blockTypes: ['footer'] }] },
    ],
  },
  {
    id: 'minimal',
    label: 'Minimal / One-page',
    description: 'Hero, about, CTA, footer — the simplest possible single-page site.',
    sections: [
      { slotKey: 'hero', split: 'full', columns: [{ blockTypes: ['hero'] }] },
      { slotKey: 'about', split: 'full', columns: [{ blockTypes: ['about'] }] },
      { slotKey: 'cta', split: 'full', columns: [{ blockTypes: ['cta'] }] },
      { slotKey: 'footer', split: 'full', columns: [{ blockTypes: ['footer'] }] },
    ],
  },
];

export function getTemplate(id: LayoutTemplateId): LayoutTemplate | undefined {
  if (
    id === 'legacy-stack' ||
    id === 'starter-stack' ||
    id === 'ai-generated' ||
    id === 'sub-page-default'
  ) {
    return undefined;
  }
  return LAYOUT_TEMPLATES.find((t) => t.id === id);
}

// =====================================================================
// Starter stack — the "build it yourself" path from the empty-page chooser.
// =====================================================================
//
// Single-column, four-block default that gives the owner a real page
// to start editing instead of staring at an empty canvas. No AI used.
// Owner picks this when they know they want to write the content
// themselves and don't need a vertical-specific template.

export const STARTER_STACK_SPEC: readonly TemplateSectionSpec[] = [
  { slotKey: 'hero', split: 'full', columns: [{ blockTypes: ['hero'] }] },
  { slotKey: 'about', split: 'full', columns: [{ blockTypes: ['about'] }] },
  { slotKey: 'contact', split: 'full', columns: [{ blockTypes: ['contact'] }] },
  { slotKey: 'footer', split: 'full', columns: [{ blockTypes: ['footer'] }] },
];

// =====================================================================
// Starter-stack variants (3B-3.5d v2).
// =====================================================================
//
// Three preset block compositions surfaced in the starter-stack picker
// modal. All variants share `layoutTemplate: 'starter-stack'` once
// applied — owner can switch between them via the "Start over" flow.

export type StarterStackVariantId = 'minimal' | 'standard' | 'rich';

export interface StarterStackVariant {
  id: StarterStackVariantId;
  label: string;
  description: string;
  // Each entry is one full-width section with one block.
  blockTypes: readonly BlockType[];
}

export const STARTER_STACK_VARIANTS: readonly StarterStackVariant[] = [
  {
    id: 'minimal',
    label: 'Minimal one-pager',
    description: 'Just enough to introduce your business and point visitors at the next step.',
    blockTypes: ['hero', 'cta', 'footer'],
  },
  {
    id: 'standard',
    label: 'Standard',
    description: 'A balanced starter for most service businesses — hero, about, contact, footer.',
    blockTypes: ['hero', 'about', 'contact', 'footer'],
  },
  {
    id: 'rich',
    label: 'Rich',
    description: 'More content slots to fill in — services and testimonials in addition to the basics.',
    blockTypes: ['hero', 'about', 'services', 'testimonials', 'contact', 'footer'],
  },
];

export function getStarterStackVariant(
  id: StarterStackVariantId,
): StarterStackVariant {
  const found = STARTER_STACK_VARIANTS.find((v) => v.id === id);
  if (!found) throw new Error(`Unknown starter-stack variant: ${id}`);
  return found;
}

// =====================================================================
// Sub-page default — applied when a new non-home page is created.
// =====================================================================
//
// Owner-driven sub-pages (About, Flavors, Contact, etc.) skip the
// chooser entirely and open straight to a generic skeleton that
// echoes the home page's rhythm. Owner replaces each section's
// content for their specific topic.
//
// Shape: hero (topic header) → text (body, single full-width so the
// owner can split into 2-1 later if they want a sidebar) → cta →
// footer. Mirrors the most common page pattern; deliberately
// minimal so it's quick to edit.

export const SUB_PAGE_DEFAULT_SPEC: readonly TemplateSectionSpec[] = [
  { slotKey: 'hero', split: 'full', columns: [{ blockTypes: ['hero'] }] },
  { slotKey: 'body', split: 'full', columns: [{ blockTypes: ['text'] }] },
  { slotKey: 'cta', split: 'full', columns: [{ blockTypes: ['cta'] }] },
  { slotKey: 'footer', split: 'full', columns: [{ blockTypes: ['footer'] }] },
];

// =====================================================================
// Topic-aware sub-page specs.
// =====================================================================
//
// When the operator names a page with a recognized slug segment we
// seed a topic-appropriate skeleton instead of the generic default.
// `pickSubPageSpec(slug)` does the routing. Match logic:
//   1. Exact full-slug match (e.g. slug 'contact-us' → contact spec)
//   2. Hyphen-separated segment match (e.g. slug 'our-services'
//      → segments ['our','services'] → services spec)
//   3. Fall through to SUB_PAGE_DEFAULT_SPEC.

export const CONTACT_PAGE_SPEC: readonly TemplateSectionSpec[] = [
  { slotKey: 'hero', split: 'full', columns: [{ blockTypes: ['hero'] }] },
  { slotKey: 'contact', split: 'full', columns: [{ blockTypes: ['contact'] }] },
  { slotKey: 'footer', split: 'full', columns: [{ blockTypes: ['footer'] }] },
];

export const ABOUT_PAGE_SPEC: readonly TemplateSectionSpec[] = [
  { slotKey: 'hero', split: 'full', columns: [{ blockTypes: ['hero'] }] },
  { slotKey: 'about', split: 'full', columns: [{ blockTypes: ['about'] }] },
  { slotKey: 'cta', split: 'full', columns: [{ blockTypes: ['cta'] }] },
  { slotKey: 'footer', split: 'full', columns: [{ blockTypes: ['footer'] }] },
];

export const SERVICES_PAGE_SPEC: readonly TemplateSectionSpec[] = [
  { slotKey: 'hero', split: 'full', columns: [{ blockTypes: ['hero'] }] },
  { slotKey: 'services', split: 'full', columns: [{ blockTypes: ['services'] }] },
  { slotKey: 'cta', split: 'full', columns: [{ blockTypes: ['cta'] }] },
  { slotKey: 'footer', split: 'full', columns: [{ blockTypes: ['footer'] }] },
];

export const GALLERY_PAGE_SPEC: readonly TemplateSectionSpec[] = [
  { slotKey: 'hero', split: 'full', columns: [{ blockTypes: ['hero'] }] },
  { slotKey: 'gallery', split: 'full', columns: [{ blockTypes: ['gallery'] }] },
  { slotKey: 'cta', split: 'full', columns: [{ blockTypes: ['cta'] }] },
  { slotKey: 'footer', split: 'full', columns: [{ blockTypes: ['footer'] }] },
];

export const FAQ_PAGE_SPEC: readonly TemplateSectionSpec[] = [
  { slotKey: 'hero', split: 'full', columns: [{ blockTypes: ['hero'] }] },
  { slotKey: 'faq', split: 'full', columns: [{ blockTypes: ['faq'] }] },
  { slotKey: 'cta', split: 'full', columns: [{ blockTypes: ['cta'] }] },
  { slotKey: 'footer', split: 'full', columns: [{ blockTypes: ['footer'] }] },
];

export const TEAM_PAGE_SPEC: readonly TemplateSectionSpec[] = [
  { slotKey: 'hero', split: 'full', columns: [{ blockTypes: ['hero'] }] },
  { slotKey: 'team', split: 'full', columns: [{ blockTypes: ['team'] }] },
  { slotKey: 'cta', split: 'full', columns: [{ blockTypes: ['cta'] }] },
  { slotKey: 'footer', split: 'full', columns: [{ blockTypes: ['footer'] }] },
];

// Pricing uses the dedicated `pricing` block (3B-3.5g+) for tier cards —
// real tier name + price + period + feature checklist + per-tier CTA —
// followed by an FAQ band that answers the standard "what's included /
// what's the trial / can I cancel" questions inline.
export const PRICING_PAGE_SPEC: readonly TemplateSectionSpec[] = [
  { slotKey: 'hero', split: 'full', columns: [{ blockTypes: ['hero'] }] },
  { slotKey: 'tiers', split: 'full', columns: [{ blockTypes: ['pricing'] }] },
  { slotKey: 'faq', split: 'full', columns: [{ blockTypes: ['faq'] }] },
  { slotKey: 'cta', split: 'full', columns: [{ blockTypes: ['cta'] }] },
  { slotKey: 'footer', split: 'full', columns: [{ blockTypes: ['footer'] }] },
];

// Quote-led spec for contractors, consultants, photographers — any
// service business that custom-prices each job. Hero sets a "starting
// at" expectation; about explains the custom-quote approach; services
// (with prices) shows sample projects + ranges; testimonials build
// credibility; contact form drives the quote request.
export const ESTIMATES_PAGE_SPEC: readonly TemplateSectionSpec[] = [
  { slotKey: 'hero', split: 'full', columns: [{ blockTypes: ['hero'] }] },
  { slotKey: 'trust-strip', split: 'full', columns: [{ blockTypes: ['credentials'] }] },
  { slotKey: 'approach', split: 'full', columns: [{ blockTypes: ['about'] }] },
  { slotKey: 'sample-projects', split: 'full', columns: [{ blockTypes: ['services'] }] },
  { slotKey: 'testimonials', split: 'full', columns: [{ blockTypes: ['testimonials'] }] },
  { slotKey: 'contact', split: 'full', columns: [{ blockTypes: ['contact'] }] },
  { slotKey: 'footer', split: 'full', columns: [{ blockTypes: ['footer'] }] },
];

// Keywords mapped onto specs. A slug "matches" a keyword when EITHER
// the entire slug equals the keyword OR one of its hyphen-separated
// segments does. Order in the keys doesn't matter; the first hit wins.
const SUB_PAGE_KEYWORDS: ReadonlyArray<
  readonly [readonly string[], readonly TemplateSectionSpec[]]
> = [
  [
    ['contact', 'hello', 'reach', 'locations', 'location', 'find-us', 'where'],
    CONTACT_PAGE_SPEC,
  ],
  [['about', 'story'], ABOUT_PAGE_SPEC],
  [
    ['estimates', 'estimate', 'quote', 'quotes', 'get-a-quote', 'request-quote'],
    ESTIMATES_PAGE_SPEC,
  ],
  [['pricing', 'plans', 'tiers', 'price'], PRICING_PAGE_SPEC],
  [['services', 'service', 'menu', 'products', 'offerings'], SERVICES_PAGE_SPEC],
  [['gallery', 'portfolio', 'work', 'photos', 'projects'], GALLERY_PAGE_SPEC],
  [['faq', 'faqs', 'questions', 'help', 'support'], FAQ_PAGE_SPEC],
  [['team', 'crew', 'people', 'staff'], TEAM_PAGE_SPEC],
];

export function pickSubPageSpec(slug: string): readonly TemplateSectionSpec[] {
  const normalized = slug.trim().toLowerCase();
  if (!normalized) return SUB_PAGE_DEFAULT_SPEC;
  const segments = normalized.split('-').filter(Boolean);
  for (const [keywords, spec] of SUB_PAGE_KEYWORDS) {
    if (keywords.includes(normalized)) return spec;
    for (const seg of segments) {
      if (keywords.includes(seg)) return spec;
    }
  }
  return SUB_PAGE_DEFAULT_SPEC;
}

// =====================================================================
// Block-width constraints — which column splits each block type fits.
// =====================================================================
//
// The picker filters by target column width via this table. Drop
// validation in cross-column drag uses the same source of truth.

export type ColumnWidth = 'full' | '2-3' | '1-2' | '1-3';

export function columnWidthAtIndex(split: SectionSplit, columnIndex: number): ColumnWidth {
  if (split === 'full') return 'full';
  if (split === '1-1') return '1-2';
  if (split === '1-1-1') return '1-3';
  if (split === '2-1') return columnIndex === 0 ? '2-3' : '1-3';
  if (split === '1-2') return columnIndex === 0 ? '1-3' : '2-3';
  return 'full';
}

export const ALLOWED_COLUMN_WIDTHS: Record<BlockType, readonly ColumnWidth[]> = {
  hero: ['full'],
  footer: ['full'],
  services: ['full'],
  pricing: ['full'],
  gallery: ['full'],
  testimonials: ['full'],
  team: ['full'],
  faq: ['full'],
  credentials: ['full'],
  locations: ['full'],
  about: ['full', '2-3', '1-2'],
  contact: ['full', '2-3', '1-2'],
  cta: ['full', '2-3', '1-2', '1-3'],
  heading: ['full', '2-3', '1-2', '1-3'],
  text: ['full', '2-3', '1-2', '1-3'],
};

export function isBlockAllowedInColumn(
  blockType: BlockType,
  split: SectionSplit,
  columnIndex: number,
): boolean {
  const width = columnWidthAtIndex(split, columnIndex);
  return ALLOWED_COLUMN_WIDTHS[blockType].includes(width);
}
